import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';

interface Comment {
  id: string;
  name: string;
  comment: string;
  created_at: string;
}

interface Props {
  newsId: string;
}

export default function CommentSection({ newsId }: Props) {
  const [comments, setComments] = useState<Comment[]>([]);
  const [form, setForm] = useState({ name: '', email: '', comment: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!newsId) return;
    supabase
      .from('news_comments')
      .select('id, name, comment, created_at')
      .eq('news_id', newsId)
      .eq('approved', true)
      .order('created_at', { ascending: false })
      .then(({ data }) => setComments(data || []));
  }, [newsId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.comment.trim()) {
      setError('Lütfen tüm alanları doldurun.');
      return;
    }
    setLoading(true);
    setError('');
    const { error: err } = await supabase.from('news_comments').insert([{
      news_id: newsId,
      name: form.name.trim(),
      email: form.email.trim(),
      comment: form.comment.trim(),
      approved: false,
    }]);
    setLoading(false);
    if (err) { setError('Bir hata oluştu, tekrar deneyin.'); return; }
    setSuccess(true);
    setForm({ name: '', email: '', comment: '' });
  };

  const formatDate = (d: string) => new Date(d).toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' });

  return (
    <section className="mt-12 pt-10 border-t border-white/10">
      <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
        <i className="ri-chat-3-line text-[#00D9FF]" />
        Yorumlar
        {comments.length > 0 && (
          <span className="text-sm font-normal text-white/40">({comments.length})</span>
        )}
      </h2>

      {/* Yorum Listesi */}
      {comments.length > 0 ? (
        <div className="flex flex-col gap-4 mb-10">
          {comments.map(c => (
            <div key={c.id} className="bg-white/5 border border-white/10 rounded-xl p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 flex items-center justify-center bg-[#00D9FF]/10 rounded-full flex-shrink-0">
                  <i className="ri-user-line text-[#00D9FF] text-sm" />
                </div>
                <div>
                  <div className="text-white font-semibold text-sm">{c.name}</div>
                  <div className="text-white/40 text-xs">{formatDate(c.created_at)}</div>
                </div>
              </div>
              <p className="text-white/70 text-sm leading-relaxed">{c.comment}</p>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 mb-8 bg-white/5 border border-white/10 rounded-xl">
          <i className="ri-chat-off-line text-3xl text-white/20 block mb-2" />
          <p className="text-white/40 text-sm">Henüz yorum yok. İlk yorumu sen yap!</p>
        </div>
      )}

      {/* Yorum Formu */}
      {success ? (
        <div className="bg-[#00FF88]/10 border border-[#00FF88]/30 rounded-xl p-6 text-center">
          <div className="w-12 h-12 flex items-center justify-center bg-[#00FF88]/20 rounded-full mx-auto mb-3">
            <i className="ri-check-line text-xl text-[#00FF88]" />
          </div>
          <h3 className="text-white font-bold mb-1">Yorumunuz Alındı!</h3>
          <p className="text-white/50 text-sm">İncelendikten sonra yayınlanacak.</p>
          <button onClick={() => setSuccess(false)} className="mt-4 text-sm text-[#00D9FF] hover:underline cursor-pointer whitespace-nowrap">
            Yeni yorum yaz
          </button>
        </div>
      ) : (
        <div className="bg-white/5 border border-white/10 rounded-xl p-6">
          <h3 className="text-white font-bold text-base mb-5">Yorum Bırakın</h3>
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-white/50 mb-1">Ad Soyad <span className="text-red-400">*</span></label>
                <input
                  type="text"
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="Adınız Soyadınız"
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#00D9FF]"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-white/50 mb-1">E-posta <span className="text-red-400">*</span></label>
                <input
                  type="email"
                  value={form.email}
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  placeholder="ornek@email.com"
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#00D9FF]"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-white/50 mb-1">Yorumunuz <span className="text-red-400">*</span></label>
              <textarea
                value={form.comment}
                onChange={e => setForm(f => ({ ...f, comment: e.target.value.slice(0, 500) }))}
                rows={4}
                maxLength={500}
                placeholder="Düşüncelerinizi paylaşın..."
                className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#00D9FF] resize-none"
              />
              <div className="text-xs text-white/30 text-right mt-1">{form.comment.length}/500</div>
            </div>
            {error && <div className="text-sm text-red-400 bg-red-500/10 rounded-lg px-4 py-2.5">{error}</div>}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#00D9FF] text-[#0A0E27] rounded-lg py-3 text-sm font-bold hover:bg-[#00D9FF]/90 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
            >
              {loading ? 'Gönderiliyor...' : 'Yorum Gönder'}
            </button>
            <p className="text-xs text-white/30 text-center">Yorumunuz incelendikten sonra yayınlanacaktır.</p>
          </form>
        </div>
      )}
    </section>
  );
}
